import { PlusIcon, XIcon } from "lucide-react";
import { useMemo, useRef } from "react";

import { ExpectedToolsInput } from "./ExpectedToolsInput";

import { Button } from "@/components/ui/button";
import useAutoScroll from "@/hooks/useAutoScroll";

interface MultipleScenariosProps {
  scenarios: string[][];
  onScenariosChange: (scenarios: string[][]) => void;
  suggestions: string[];
  hasTraceSource: boolean;
  disabled?: boolean;
}

export const MultipleScenarios = ({
  scenarios,
  onScenariosChange,
  suggestions,
  hasTraceSource,
  disabled = false,
}: MultipleScenariosProps) => {
  const normalizedScenarios = useMemo(() => (scenarios.length > 0 ? scenarios : [[]]), [scenarios]);
  const ref = useRef<HTMLDivElement>(null);
  const scenarioItems = useMemo(
    () =>
      normalizedScenarios.map((scenario, index) => ({
        id: `scenario-${index}`,
        value: scenario,
      })),
    [normalizedScenarios],
  );
  const { lastItemRef, scrollContainerRef } = useAutoScroll(scenarioItems, ref);

  const updateScenario = (index: number, tools: string[]) => {
    const next = normalizedScenarios.map((scenario, scenarioIndex) =>
      scenarioIndex === index ? tools : scenario,
    );
    onScenariosChange(next);
  };

  const removeScenario = (index: number) => {
    if (normalizedScenarios.length <= 1) {
      onScenariosChange([[]]);
      return;
    }
    onScenariosChange(normalizedScenarios.filter((_, scenarioIndex) => scenarioIndex !== index));
  };

  const addScenario = () => {
    onScenariosChange([...normalizedScenarios, []]);
  };

  return (
    <div className="flex w-full min-w-0 flex-col gap-y-2">
      <div
        ref={ref}
        className="max-h-[170px] w-full min-w-0 overflow-y-auto p-1"
        data-testid="scenario-list"
      >
        <div ref={scrollContainerRef} className="flex w-full flex-col gap-y-2">
          {normalizedScenarios.map((scenario, index) => (
            <div
              // eslint-disable-next-line react/no-array-index-key -- scenario order is the identity
              key={index}
              ref={index === normalizedScenarios.length - 1 ? lastItemRef : null}
              className="flex w-full min-w-0 items-center gap-x-2"
            >
              <ExpectedToolsInput
                value={scenario}
                onChange={tools => updateScenario(index, tools)}
                suggestions={suggestions}
                hasTraceSource={hasTraceSource}
                disabled={disabled}
                showHelperText={false}
              />
              {!disabled ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 shrink-0 text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                  onClick={() => removeScenario(index)}
                  aria-label="Remove scenario"
                >
                  <XIcon className="h-4 w-4" />
                </Button>
              ) : null}
            </div>
          ))}
        </div>
      </div>
      {!disabled ? (
        <Button type="button" variant="ghostBlue" size="xs" onClick={addScenario}>
          <PlusIcon className="mr-1 h-3 w-3" />
          Add scenario
        </Button>
      ) : null}
    </div>
  );
};
